import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import AdminLayout from "@/components/admin-layout";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { toast } from "@/components/ui/use-toast";
import { User, ChevronLeft, ChevronRight, Loader2, Search, AlertTriangle, X } from "lucide-react";
import { useState, useEffect } from "react";
import { useDebounce } from "@/lib/hooks/use-debounce";

interface User {
  id: number;
  email: string;
  displayName: string | null;
  avatarUrl: string | null;
  poolToken: string | null;
  cacatais: number;
  suspicious: boolean;
  createdAt: string;
}

interface PaginatedResponse {
  users: User[];
  total: number;
  page: number;
  pageSize: number;
}

const PAGE_SIZE = 10;

export default function AdminUsersPage() {
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const debouncedSearch = useDebounce(searchTerm, 500);

  // Reset page when search term changes
  useEffect(() => {
    setPage(1);
  }, [debouncedSearch]);

  // Mutation to update user suspicious status
  const suspiciousMutation = useMutation({
    mutationFn: async ({ userId, suspicious }: { userId: number; suspicious: boolean }) => {
      const response = await fetch(`/api/admin/users/${userId}/suspicious`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ suspicious }),
      });

      if (!response.ok) {
        throw new Error('Failed to update user status');
      }

      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/users"] });
      toast({
        title: "User updated",
        description: "User suspicious status updated successfully",
      });
    },
    onError: () => {
      toast({
        title: "Error",
        description: "Failed to update user status",
        variant: "destructive",
      });
    },
  });

  const handleResetSearch = () => {
    setSearchTerm("");
  };

  const { data, isLoading } = useQuery<PaginatedResponse>({
    queryKey: ["/api/admin/users", { page, search: debouncedSearch }],
    queryFn: async () => {
      const searchParam = debouncedSearch ? `&search=${encodeURIComponent(debouncedSearch)}` : '';
      const response = await fetch(`/api/admin/users?page=${page}${searchParam}`);
      if (!response.ok) {
        throw new Error('Failed to fetch users');
      }
      return response.json();
    },
  });

  const totalPages = data ? Math.ceil(data.total / PAGE_SIZE) : 0;

  return (
    <AdminLayout>
      <Card className="bg-zinc-900 border-zinc-800">
        <CardHeader>
          <CardTitle>Users</CardTitle>
          <CardDescription>Manage registered users</CardDescription>
          <div className="mt-4 flex items-center space-x-2">
            <div className="relative flex-1 max-w-sm">
              <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-zinc-500" />
              <Input
                type="search"
                placeholder="Search users by name or email..."
                className="pl-8 bg-zinc-800 border-zinc-700"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              {searchTerm && (
                <Button
                  variant="ghost"
                  size="icon"
                  className="absolute right-1 top-1 h-7 w-7 text-zinc-500 hover:text-zinc-300"
                  onClick={handleResetSearch}
                >
                  <X className="h-4 w-4" />
                </Button>
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex justify-center items-center py-8">
              <Loader2 className="h-6 w-6 animate-spin" />
            </div>
          ) : (
            <>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>User</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Pool Token</TableHead>
                    <TableHead>Cacatais</TableHead>
                    <TableHead>Joined</TableHead>
                    <TableHead>Suspicious</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data?.users.map((user) => (
                    <TableRow key={user.id}>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Avatar className="h-8 w-8">
                            <AvatarImage src={user.avatarUrl || undefined} />
                            <AvatarFallback>
                              <User className="h-4 w-4" />
                            </AvatarFallback>
                          </Avatar>
                          <span>{user.displayName || "Anonymous"}</span>
                        </div>
                      </TableCell>
                      <TableCell>{user.email}</TableCell>
                      <TableCell>
                        {user.poolToken ? (
                          <code className="bg-zinc-800 px-2 py-1 rounded text-xs">
                            {user.poolToken}
                          </code>
                        ) : (
                          <span className="text-zinc-500">No token</span>
                        )}
                      </TableCell>
                      <TableCell>{user.cacatais}</TableCell>
                      <TableCell>
                        {new Date(user.createdAt).toLocaleDateString()}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Switch 
                            checked={user.suspicious} 
                            onCheckedChange={(checked) => {
                              suspiciousMutation.mutate({
                                userId: user.id,
                                suspicious: checked
                              });
                            }}
                          />
                          {user.suspicious && (
                            <AlertTriangle className="h-4 w-4 text-amber-500" />
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              <div className="flex items-center justify-between mt-4 space-x-2">
                <div className="text-sm text-zinc-500">
                  {data && (
                    <>
                      {data.total === 0 ? (
                        "No users found"
                      ) : (
                        <>
                          Page {page} of {totalPages} • {data.total} user{data.total !== 1 ? "s" : ""}
                          {debouncedSearch && ` matching "${debouncedSearch}"`}
                        </>
                      )}
                    </>
                  )}
                </div>
                <div className="flex items-center space-x-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={page === 1}
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                    disabled={page === totalPages}
                  >
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </AdminLayout>
  );
}